import React, { useState } from "react";
import Input from "../utils/Input";
import Button from "../utils/Button";
import { useApiQuery } from "../hooks/useApiQuery";
import toast from "react-hot-toast";

type ForgotPasswordResponse = {
  message?: string;
};

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    refetch,
    isFetching,
  } = useApiQuery<ForgotPasswordResponse>({
    queryKey: ["forgot-password"],
    enabled: false,
    retry: false,
    request: {
      method: "POST",
      url: "/auth/forgot-password",
      data: { email },
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (!email) {
      setFormError("Email is required");
      return;
    }

    const loadingId = toast.loading("Sending reset email...");

    try {
      const result = await refetch({ throwOnError: true });
      if (result?.data) {
        setShowSuccessModal(true);
      }
      toast.success("If an account exists, a reset email has been sent.");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to send reset email. Please try again.";
      setFormError(message);
      toast.error(message);
    } finally {
      toast.dismiss(loadingId);
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="bg-gray-900 p-8 rounded-lg shadow-lg w-full max-w-md animate-fireGlow"
      >
        <h2 className="text-2xl text-white font-bold mb-6 text-center">Forgot Password</h2>
        <p className="text-sm text-white text-center pb-5 m-auto">
          Enter the email associated with your account and we&apos;ll send reset instructions.
        </p>

        {formError && <p className="text-red-500 text-sm mb-4">{formError}</p>}

        <Input
          label="Email"
          name="email"
          placeholder="email@example.com"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          debounce={0}
          required
          className="mb-6"
          onValidate={(val) => {
            if (!val) return "Email is required";
            const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
            return ok ? null : "Invalid email address";
          }}
        />

        <Button
          disabled={!email || isFetching}
          label={isFetching ? "Sending..." : "Send Reset Email"}
          type="submit"
          variant="primary"
          size="extra-large"
        />
      </form>

      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 px-4">
          <div className="bg-gray-900 text-white p-6 rounded-lg shadow-2xl w-full max-w-md">
            <h3 className="text-xl font-semibold mb-3 text-center">Check your email</h3>
            <p className="text-sm text-gray-300 text-center mb-6">
              We&apos;ve sent password reset instructions to {email}. Please check your inbox and spam folder.
            </p>
            <Button
              label="Got it"
              variant="primary"
              size="extra-large"
              onClick={() => setShowSuccessModal(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
